import React, { useEffect, useRef } from "react";
import "./about.css";
import { ASSETS, CALENDLY_URL, PRODUCTION_URL } from "../config";
import { usePageSeo, absoluteUrl, ORGANISATION_JSON_LD } from "../useSeo";
import { Header, BookACallLink } from "../Header";
import { FloatingCta, Footer } from "../FooterAndFloating";

const DESCRIPTION =
  "Meet Craig “Doe” Wilmann, founder of The Business Playwright, bringing fifteen years of business experience together with professional theatre and storytelling.";

const PAGE = {
  title: "About Craig “Doe” Wilmann | The Business Playwright",
  description: DESCRIPTION,
  path: "/about",
  ogImage: ASSETS.craigPortrait,
  ogImageAlt: "Craig “Doe” Wilmann, founder of The Business Playwright",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About Craig “Doe” Wilmann",
    url: absoluteUrl("/about"),
    description: DESCRIPTION,
    isPartOf: { "@type": "WebSite", name: "The Business Playwright", url: PRODUCTION_URL },
    publisher: ORGANISATION_JSON_LD,
    mainEntity: {
      "@type": "Person",
      name: "Craig Wilmann",
      alternateName: "Doe Wilmann",
      jobTitle: "Founder",
      worksFor: ORGANISATION_JSON_LD,
      image: absoluteUrl(ASSETS.craigPortrait),
      email: "mailto:craig@loveyourwork.uk",
    },
    potentialAction: { "@type": "ReserveAction", target: CALENDLY_URL },
  },
};

export default function AboutPage() {
  usePageSeo(PAGE);
  const heroRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bp-site ab-page" data-testid="about-page">
      <Header />
      <main id="main">
        <section className="ab-hero" ref={heroRef} aria-labelledby="ab-hero-title" data-testid="ab-hero">
          <div className="bp-wrap ab-hero-grid">
            <div className="ab-hero-copy">
              <span className="bp-hero-rule" aria-hidden="true" />
              <h1 id="ab-hero-title" className="ab-hero-title" data-testid="ab-hero-title">
                <span className="ab-hero-name">Hi, I’m Craig “Doe” Wilmann</span>, founder of The Business Playwright. For the past fifteen years I have built businesses while writing and producing stage plays on the side. I'm now bringing those two things together.
              </h1>
            </div>
            <div className="ab-portrait-wrap">
              <picture>
                <source type="image/webp" srcSet={ASSETS.craigPortraitWeb} media="(max-width: 899px)" />
                <img
                  src={ASSETS.craigPortrait}
                  alt="Craig “Doe” Wilmann, founder of The Business Playwright"
                  width="1254"
                  height="1254"
                  className="ab-portrait"
                  fetchPriority="high"
                  decoding="async"
                  data-testid="ab-portrait"
                />
              </picture>
            </div>
          </div>
        </section>

        <section className="bp-scene ab-essay" aria-label="About The Business Playwright" data-testid="ab-essay">
          <div className="bp-wrap ab-column">
            <p>The online world is increasingly becoming by robots for robots. Social media, which was never that social to begin with, is eating itself with fake videos, fake views and fake comments. And theatre, our oldest and most enduring art form, can provide a refuge.</p>
            <p>There’s a golden opportunity for businesses, in particular, to align themselves with one of the few areas of our world that AI cannot replace.</p>
            <p>How do you hold people's attention in a world of endless online content? Even if you're getting thousands of views, how many of those views are by real people? And how many of those people will remember what they've seen the following day?</p>
            <p className="ab-emphasis" data-testid="ab-emphasis-1">Theatre is the one place where people have to put their phones down and pay attention.</p>
            <p className="ab-emphasis ab-emphasis--secondary" data-testid="ab-emphasis-2">Your story and business mission deserve that dedicated and focused attention that theatre can deliver.</p>
            <p>Working with leading theatre actors, directors, writers, and producers, we can create a piece of professional theatre that will move people, make them think, make them laugh, and make them remember what it is that you and your business stand for.</p>
            <p>We can also deliver engaging and interactive team-building and training experiences that will stick with your team for years to come.</p>
            <p>And we offer storytelling training and consultancy on an ad hoc basis - helping you tell better, more memorable stories.</p>
          </div>
        </section>

        <section className="bp-scene ab-contact" aria-label="Contact Craig" data-testid="ab-contact">
          <div className="bp-wrap ab-column">
            <p className="ab-contact-copy" data-testid="ab-contact-copy">
              If I can be of any help at all, please email me on{" "}
              <a href="mailto:craig@loveyourwork.uk" className="ab-mail" data-testid="ab-mailto">Craig@loveyourwork.uk</a>
              {" "}or book a call here.
            </p>
            <BookACallLink testId="ab-book-a-call" />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} testId="ab-floating-cta" />
    </div>
  );
}
